import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Check, Loader2, Clock, Trash2, CalendarIcon, Layers, Image } from "lucide-react";
import { format } from "date-fns";
import BackButton from "@/components/BackButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "business", label: "Business", color: "#3b82f6" },
  { value: "wealth", label: "Wealth", color: "#f59e0b" },
  { value: "career", label: "Career", color: "#8b5cf6" },
  { value: "health", label: "Health", color: "#10b981" },
  { value: "academics", label: "Academics", color: "#06b6d4" },
  { value: "relationship", label: "Relationship", color: "#ec4899" },
  { value: "recognition", label: "Recognition", color: "#f97316" },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const displayHour = i + 1;
  return {
    value: String(displayHour),
    displayHour,
    label: `${String(displayHour).padStart(2, "0")}:00`,
  };
});

export default function CategoryUploadPage() {
  const singleFileRef = useRef(null);
  const bulkFileRefs = useRef({});

  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [hour, setHour] = useState("1");
  const [activeTab, setActiveTab] = useState("bulk");

  // Single upload
  const [category, setCategory] = useState("wealth");
  const [singleImage, setSingleImage] = useState(null);
  const [singlePreview, setSinglePreview] = useState(null);
  const [singleUploading, setSingleUploading] = useState(false);
  const [singleStatus, setSingleStatus] = useState(null);

  // Bulk upload (one image per category)
  const [bulkFiles, setBulkFiles] = useState({});
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadStatus, setBulkUploadStatus] = useState({});

  // Existing images
  const [existing, setExisting] = useState({});
  const [loadingExisting, setLoadingExisting] = useState(true);

  useEffect(() => {
    fetchExisting();
    // Clear selected files when date/hour changes
    Object.values(bulkFiles).forEach(f => { if (f?.preview) URL.revokeObjectURL(f.preview); });
    setBulkFiles({});
    setBulkUploadStatus({});
    if (singlePreview) URL.revokeObjectURL(singlePreview);
    setSingleImage(null);
    setSinglePreview(null);
    setSingleStatus(null);
  }, [date, hour]);

  const fetchExisting = async () => {
    setLoadingExisting(true);
    try {
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const d = date.getDate();
      const res = await fetch(`${API}/category/available?year=${y}&month=${m}&day=${d}&hour=${hour}`);
      const data = await res.json();
      if (data.success) {
        setExisting(data.available || {});
      }
    } catch (err) {
      console.error("Failed to fetch category images:", err);
    } finally {
      setLoadingExisting(false);
    }
  };

  // Single upload
  const handleSingleFileSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      setSingleImage(file);
      setSinglePreview(URL.createObjectURL(file));
      setSingleStatus(null);
    }
  };

  const handleSingleUpload = async () => {
    if (!singleImage) return;
    setSingleUploading(true);
    setSingleStatus(null);

    const formData = new FormData();
    formData.append("file", singleImage);
    formData.append("year", String(date.getFullYear()));
    formData.append("month", String(date.getMonth() + 1));
    formData.append("day", String(date.getDate()));
    formData.append("hour", hour);
    formData.append("category", category);

    try {
      const res = await fetch(`${API}/category/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        const catLabel = CATEGORIES.find(c => c.value === category)?.label || category;
        setSingleStatus({ type: "success", message: `${catLabel} image uploaded for ${format(date, "MMM d")} ${hour.padStart(2, "0")}:00` });
        fetchExisting();
        clearSingleImage();
      } else {
        setSingleStatus({ type: "error", message: data.error || "Upload failed" });
      }
    } catch {
      setSingleStatus({ type: "error", message: "Network error" });
    } finally {
      setSingleUploading(false);
    }
  };

  const clearSingleImage = () => {
    if (singlePreview) URL.revokeObjectURL(singlePreview);
    setSingleImage(null);
    setSinglePreview(null);
    if (singleFileRef.current) singleFileRef.current.value = "";
  };

  // Bulk upload - drag & drop multiple files, auto-assign to categories
  const [bulkDragActive, setBulkDragActive] = useState(false);
  const bulkFileRef = useRef(null);

  const assignBulkFiles = (selectedFiles) => {
    const imageFiles = Array.from(selectedFiles).filter(f => f.type.startsWith("image/"));
    const newFiles = {};
    imageFiles.slice(0, 7).forEach((file, index) => {
      const cat = CATEGORIES[index].value;
      newFiles[cat] = { file, preview: URL.createObjectURL(file) };
    });
    setBulkFiles(newFiles);
    setBulkUploadStatus({});
  };

  const clearBulkFiles = () => {
    Object.values(bulkFiles).forEach(f => { if (f?.preview) URL.revokeObjectURL(f.preview); });
    setBulkFiles({});
    setBulkUploadStatus({});
    if (bulkFileRef.current) bulkFileRef.current.value = "";
  };

  const handleBulkUpload = async () => {
    const cats = Object.keys(bulkFiles);
    if (cats.length === 0) return;
    setBulkUploading(true);
    setBulkUploadStatus({});

    for (const cat of cats) {
      try {
        setBulkUploadStatus(prev => ({ ...prev, [cat]: "uploading" }));
        const formData = new FormData();
        formData.append("file", bulkFiles[cat].file);
        formData.append("year", String(date.getFullYear()));
        formData.append("month", String(date.getMonth() + 1));
        formData.append("day", String(date.getDate()));
        formData.append("hour", hour);
        formData.append("category", cat);

        const res = await fetch(`${API}/category/upload`, { method: "POST", body: formData });
        const data = await res.json();
        setBulkUploadStatus(prev => ({ ...prev, [cat]: data.success ? "success" : "error" }));
      } catch {
        setBulkUploadStatus(prev => ({ ...prev, [cat]: "error" }));
      }
    }
    setBulkUploading(false);
    fetchExisting();
  };

  const handleDelete = async (cat) => {
    const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
    if (!window.confirm(`Delete ${catLabel} image?`)) return;
    try {
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const d = date.getDate();
      const res = await fetch(`${API}/category/delete?year=${y}&month=${m}&day=${d}&hour=${hour}&category=${cat}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) fetchExisting();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const bulkSelectedCount = Object.keys(bulkFiles).length;
  const bulkSuccessCount = Object.values(bulkUploadStatus).filter(s => s === "success").length;

  return (
    <div className="upload-page">
      <div className="upload-bg-pattern" />
      <div className="upload-container">
        <BackButton />

        <div className="upload-header">
          <h1 className="upload-title">Category Forecasts</h1>
          <p className="upload-subtitle">
            Upload forecast images per category for a specific date and hour
          </p>
        </div>

        {/* Date + Hour */}
        <div className="upload-datetime">
          <div className="time-selector">
            <label><CalendarIcon size={16} /> Date</label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="info-date-btn" data-testid="cat-date-picker">
                  <CalendarIcon size={16} />
                  {format(date, "MMMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="calendar-popover" align="center">
                <Calendar mode="single" selected={date} onSelect={(d) => { if (d) { setDate(d); setCalendarOpen(false); } }} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
          <div className="time-selector">
            <label><Clock size={16} /> Hour Slot</label>
            <Select value={hour} onValueChange={setHour}>
              <SelectTrigger data-testid="cat-hour-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOURS.map(h => (
                  <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Upload Mode Selector */}
        <div className="bulk-upload-tabs">
          <div className="bulk-tabs-list">
            <button
              className={`bulk-tab ${activeTab === 'bulk' ? 'active' : ''}`}
              data-state={activeTab === 'bulk' ? 'active' : 'inactive'}
              onClick={() => setActiveTab('bulk')}
              data-testid="cat-bulk-tab"
            >
              <Layers size={16} /> Bulk Upload
            </button>
            <button
              className={`bulk-tab ${activeTab === 'single' ? 'active' : ''}`}
              data-state={activeTab === 'single' ? 'active' : 'inactive'}
              onClick={() => setActiveTab('single')}
              data-testid="cat-single-tab"
            >
              <Image size={16} /> Single Upload
            </button>
          </div>

          {activeTab === 'bulk' && (
            <div className="mt-2">
            {bulkSelectedCount === 0 ? (
              <div
                className={`bulk-dropzone ${bulkDragActive ? "drag-active" : ""}`}
                onDragEnter={(e) => { e.preventDefault(); setBulkDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setBulkDragActive(false); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); setBulkDragActive(false); assignBulkFiles(e.dataTransfer.files); }}
                onClick={() => bulkFileRef.current?.click()}
                data-testid="cat-bulk-dropzone"
              >
                <Upload size={48} className="dropzone-icon" />
                <p className="dropzone-text">
                  Drag & drop up to 7 category images here, or click to select
                </p>
                <p className="dropzone-hint">
                  Images assigned in order: Business, Wealth, Career, Health, Academics, Relationship, Recognition
                </p>
                <input
                  ref={bulkFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => assignBulkFiles(e.target.files)}
                  className="hidden-input"
                  data-testid="cat-bulk-file-input"
                />
              </div>
            ) : (
              <div className="bulk-files-container">
                <div className="bulk-files-header">
                  <span className="bulk-files-count">
                    {bulkSelectedCount} category image{bulkSelectedCount !== 1 ? "s" : ""} selected
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearBulkFiles} disabled={bulkUploading} data-testid="clear-bulk-cat-btn">
                    <X size={16} /> Clear All
                  </Button>
                </div>

                <div className="bulk-preview-grid">
                  {CATEGORIES.filter(c => bulkFiles[c.value]).map(c => (
                    <div key={c.value} className={`bulk-preview-item ${bulkUploadStatus[c.value] || ""}`} data-testid={`cat-preview-${c.value}`}>
                      <div className="preview-header" style={{ borderLeftColor: c.color }}>
                        <span className="preview-time">{c.label}</span>
                        {bulkUploadStatus[c.value] === "success" && <Check size={14} className="status-icon success" />}
                        {bulkUploadStatus[c.value] === "uploading" && <Loader2 size={14} className="status-icon uploading animate-spin" />}
                        {bulkUploadStatus[c.value] === "error" && <X size={14} className="status-icon error" />}
                      </div>
                      <div className="preview-image-wrap">
                        <img src={bulkFiles[c.value].preview} alt={c.label} />
                      </div>
                      <span className="preview-filename">{bulkFiles[c.value].file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bulk-upload-actions">
              <Button className="upload-submit-btn" onClick={handleBulkUpload} disabled={bulkSelectedCount === 0 || bulkUploading} data-testid="cat-bulk-upload-btn">
                {bulkUploading ? (
                  <><Loader2 size={18} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={18} /> Upload {bulkSelectedCount} Category Image{bulkSelectedCount !== 1 ? "s" : ""}</>
                )}
              </Button>
            </div>
            {bulkSuccessCount > 0 && (
              <div className="bulk-upload-success">
                <p className="success-message">{bulkSuccessCount} category image{bulkSuccessCount !== 1 ? "s" : ""} uploaded!</p>
              </div>
            )}
            </div>
          )}

          {activeTab === 'single' && (
            <div className="mt-2">
            <div className="upload-datetime">
              <div className="time-selector">
                <label>Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="cat-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div
              className={`upload-dropzone ${singlePreview ? "has-image" : ""}`}
              onClick={() => !singlePreview && singleFileRef.current?.click()}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleSingleFileSelect(e.dataTransfer.files[0]); }}
            >
              <input ref={singleFileRef} type="file" accept="image/*" className="hidden" onChange={e => handleSingleFileSelect(e.target.files[0])} data-testid="cat-single-file-input" />
              {singlePreview ? (
                <div className="upload-preview">
                  <img src={singlePreview} alt="Preview" />
                  <Button variant="ghost" className="clear-image-btn" onClick={e => { e.stopPropagation(); clearSingleImage(); setSingleStatus(null); }}>
                    <X size={20} />
                  </Button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <Upload size={48} />
                  <p>Click or drag to upload category image</p>
                </div>
              )}
            </div>

            {singleImage && (
              <Button className="upload-submit-btn" onClick={handleSingleUpload} disabled={singleUploading} data-testid="cat-single-upload-btn">
                {singleUploading ? <><Loader2 className="animate-spin" size={18} /> Uploading...</> : <><Upload size={18} /> Upload</>}
              </Button>
            )}

            {singleStatus && (
              <div className={`upload-status ${singleStatus.type}`} data-testid="cat-single-status">
                {singleStatus.type === "success" ? <Check size={18} /> : <X size={18} />}
                {singleStatus.message}
              </div>
            )}
            </div>
          )}
        </div>

        {/* Existing Category Images */}
        <div className="info-images-list">
          <h3>Category Images for {format(date, "MMM d, yyyy")} {hour.padStart(2, "0")}:00</h3>
          {loadingExisting ? (
            <p>Loading...</p>
          ) : Object.keys(existing).length === 0 ? (
            <p className="no-images">No category images uploaded for this date/hour</p>
          ) : (
            <div className="info-images-grid">
              {CATEGORIES.filter(c => existing[c.value]).map(c => (
                <div key={c.value} className="info-image-card" data-testid={`cat-existing-${c.value}`}>
                  <div className="info-image-preview">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${existing[c.value]}`} alt={c.label} />
                  </div>
                  <div className="info-image-label" style={{ color: c.color }}>
                    {c.label}
                  </div>
                  <Button variant="ghost" className="info-delete-btn" onClick={() => handleDelete(c.value)} data-testid={`cat-delete-${c.value}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <footer className="page-footer">
          <p>Qi Men Dun Jia Plotter</p>
          <p className="footer-copyright">Copyright &copy; 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.</p>
        </footer>
      </div>
    </div>
  );
}
